import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import useModuleAccess from "../hooks/useModuleAccess";

const API_URL = process.env.REACT_APP_API_URL;
const S3_BASE_URL =
  "https://instatenders-supplier-upload.s3.ap-south-1.amazonaws.com";

const PRICE_TYPE_OPTIONS = ["INR", "USD", "EUR"];

const ECATALOGUE_SORT_FIELDS = [
  "materialCode",
  "productName",
  "customerName",
  "priceType",
  "price",
  "hsnCode",
  "uom",
  "stockQuantity",
  "status",
  "createdAt",
  "updatedAt",
];

const DEFAULT_SORT_BY = "createdAt";
const DEFAULT_SORT_ORDER = "desc";
const DEFAULT_VIEW_MODE = "grid";

const EC_STAT_CARDS = [
  {
    key: "totalCount",
    label: "Total Catalogue",
    titleColor: "#127aea",
    valueColor: "#2c7fd8",
    filterValue: null,
  },
  {
    key: "activeCount",
    label: "Active",
    titleColor: "#196F3D",
    valueColor: "#27AE60",
    filterValue: "active",
  },
  {
    key: "inactiveCount",
    label: "Inactive",
    titleColor: "#922B21",
    valueColor: "#E74C3C",
    filterValue: "inactive",
  },
];

const EcStatCard = ({
  label,
  value,
  titleColor,
  valueColor,
  onClick,
  isActive,
}) => (
  <div className="col-xl-auto col-lg-auto col-md-3 col-sm-6 mb-2 stat-card-col">
    <div
      className={`card supplier-stat-card procurement-stat-card-clickable${
        isActive ? " procurement-stat-card-active" : ""
      }`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="card-body">
        <div
          className="stat-title"
          style={{
            color: titleColor,
            fontSize: "12px",
            fontWeight: "600",
          }}
        >
          {label}
        </div>
        <div
          className="stat-value"
          style={{
            color: valueColor,
            fontSize: "20px",
            fontWeight: "700",
          }}
        >
          {value ?? 0}
        </div>
      </div>
    </div>
  </div>
);

const EcStatCardSkeleton = () => (
  <div className="col-xl-auto col-lg-auto col-md-3 col-sm-6 mb-2 stat-card-col">
    <div className="card supplier-stat-card">
      <div className="card-body">
        <div className="stat-skeleton stat-skeleton-title" />
        <div className="stat-skeleton stat-skeleton-value" />
      </div>
    </div>
  </div>
);

function normalizeSortBy(value) {
  const field = String(value || "").trim();
  return ECATALOGUE_SORT_FIELDS.includes(field) ? field : DEFAULT_SORT_BY;
}

function normalizeSortOrder(value) {
  const order = String(value || "").trim().toLowerCase();
  return order === "asc" ? "asc" : "desc";
}

function normalizeViewMode(value) {
  return String(value || "").trim().toLowerCase() === "list" ? "list" : "grid";
}

function parseApiResponse(raw) {
  let data = raw;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return raw;
    }
  }
  if (data?.body != null) {
    if (typeof data.body === "string") {
      try {
        return JSON.parse(data.body);
      } catch {
        return data.body;
      }
    }
    return data.body;
  }
  return data;
}

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeApiPayload(raw) {
  let obj = raw;
  if (!obj || typeof obj !== "object") return raw;
  obj = { ...obj };
  if (typeof obj.data === "string") {
    obj.data = parseJsonIfString(obj.data);
  }
  if (
    obj.data &&
    typeof obj.data === "object" &&
    typeof obj.data.data === "string"
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) };
  }
  return obj;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const success = responseData.success;
  return (
    success === true ||
    success === "true" ||
    success === 1 ||
    success === "1"
  );
}

function extractEcataloguePayload(responseData) {
  const root = responseData;
  let dataBlock = root?.data != null ? root.data : root;

  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") {
    return { items: [], pagination: {}, sortBy: null, sortOrder: null };
  }

  if (Array.isArray(dataBlock)) {
    return {
      items: dataBlock,
      pagination:
        root.pagination && typeof root.pagination === "object"
          ? root.pagination
          : {},
      sortBy: root.sortBy || null,
      sortOrder: root.sortOrder || null,
    };
  }

  let list =
    dataBlock.ecatalogues ||
    dataBlock.eCatalogues ||
    dataBlock.catalogues ||
    dataBlock.catalogs ||
    dataBlock.items ||
    dataBlock.materials ||
    root.ecatalogues ||
    root.items;

  if (list == null && dataBlock.data != null) {
    const inner =
      typeof dataBlock.data === "string"
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data;
    if (Array.isArray(inner)) {
      list = inner;
    } else if (inner && typeof inner === "object") {
      list =
        inner.ecatalogues ||
        inner.catalogues ||
        inner.items ||
        inner.materials;
    }
  }

  if (typeof list === "string") {
    list = parseJsonIfString(list);
  }

  const pagination =
    (dataBlock && dataBlock.pagination) || root.pagination || {};

  const total =
    typeof pagination.total === "number"
      ? pagination.total
      : typeof pagination.totalCount === "number"
        ? pagination.totalCount
        : typeof dataBlock.totalCount === "number"
          ? dataBlock.totalCount
          : typeof dataBlock.total === "number"
            ? dataBlock.total
            : undefined;

  return {
    items: Array.isArray(list) ? list : [],
    pagination:
      pagination && typeof pagination === "object" && !Array.isArray(pagination)
        ? { ...pagination, ...(total != null ? { total } : {}) }
        : total != null
          ? { total }
          : {},
    sortBy: dataBlock.sortBy || root.sortBy || null,
    sortOrder: dataBlock.sortOrder || root.sortOrder || null,
  };
}

function extractCustomersList(payload) {
  const data = payload?.data != null ? payload.data : payload;
  const list =
    (Array.isArray(data?.customers) && data.customers) ||
    (Array.isArray(data?.data) && data.data) ||
    (Array.isArray(payload?.customers) && payload.customers) ||
    (Array.isArray(data) && data) ||
    [];
  return list
    .map((item) => ({
      _id: String(item?._id || item?.id || "").trim(),
      customerName: String(
        item?.customerName || item?.customer || item?.name || "",
      ).trim(),
    }))
    .filter((item) => item._id && item.customerName)
    .sort((a, b) => a.customerName.localeCompare(b.customerName));
}

function buildS3Url(s3Key) {
  const key = String(s3Key || "").trim().replace(/^\/+/, "");
  if (!key) return "";
  if (/^https?:\/\//i.test(key)) return key;
  return `${S3_BASE_URL}/${key}`;
}

function getImageUrl(item) {
  if (!item) return "";
  if (typeof item === "string") return buildS3Url(item);
  return (
    item.url ||
    item.imageUrl ||
    item.publicUrl ||
    buildS3Url(item.s3Key || item.key)
  );
}

function getItemImages(item) {
  if (!item || typeof item !== "object") return [];
  const raw = Array.isArray(item.images) ? item.images : [];
  return raw
    .map((img) => {
      const url = getImageUrl(img);
      return url ? { url } : null;
    })
    .filter(Boolean);
}

function displayValue(value) {
  if (value == null || value === "") return "-";
  return String(value);
}

function formatDateToDisplay(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return displayValue(dateString);
  return date.toLocaleDateString("en-GB", {
    timeZone: "Asia/Kolkata",
  });
}

function renderStatusBadge(status) {
  const value = String(status || "").trim();
  if (!value) return "-";
  const normalized = value.toLowerCase();
  const badgeClass =
    normalized === "active"
      ? "badge-success"
      : normalized === "inactive"
        ? "badge-danger"
        : "badge-secondary";
  return (
    <span
      className={`badge ${badgeClass}`}
      style={{ fontSize: "90%", fontWeight: 550 }}
    >
      {value}
    </span>
  );
}

function getRecordId(item) {
  return item?._id || item?.id || item?.ecatalogueId || item?.catalogueId || "";
}

function getProductName(item) {
  return (
    item?.productName ||
    item?.materialName ||
    item?.materialDescription ||
    item?.description ||
    ""
  );
}

function getCustomerPriceRows(item) {
  const list = Array.isArray(item?.customerPrices) ? item.customerPrices : [];
  if (list.length > 0) {
    return list.map((row, index) => {
      const customerRef = row?.customerId;
      const customerName =
        String(row?.customerName || "").trim() ||
        (customerRef && typeof customerRef === "object"
          ? String(customerRef.customerName || customerRef.name || "").trim()
          : "") ||
        "-";
      const partNumber = String(row?.partNumber || "").trim() || "-";
      const currency = String(row?.Currency || row?.priceType || "").trim();
      const rawPrice = row?.price ?? null;
      const price =
        rawPrice == null || rawPrice === ""
          ? "-"
          : Number.isFinite(Number(rawPrice))
            ? Number(rawPrice)
            : rawPrice;

      return {
        key: String(row?._id || row?.id || index),
        customerName,
        partNumber,
        currency,
        price,
      };
    });
  }

  const customerName = (() => {
    if (typeof item?.customerName === "string" && item.customerName.trim()) {
      return item.customerName.trim();
    }
    if (typeof item?.customer === "string" && item.customer.trim()) {
      return item.customer.trim();
    }
    if (item?.customer && typeof item.customer === "object") {
      return (
        item.customer.customerName || item.customer.name || ""
      ).trim();
    }
    return "";
  })();

  const currency = String(item?.priceType || item?.Currency || "").trim();
  const raw =
    item?.price ??
    item?.unitPrice ??
    item?.sellingPrice ??
    item?.amount ??
    null;
  const price =
    raw == null || raw === ""
      ? null
      : Number.isFinite(Number(raw))
        ? Number(raw)
        : raw;

  if (customerName || price != null) {
    return [
      {
        key: "legacy",
        customerName: customerName || "-",
        partNumber: String(item?.partNumber || item?.materialPartNumber || "").trim() || "-",
        currency,
        price: price ?? "-",
      },
    ];
  }

  return [];
}

function getCustomerName(item) {
  if (typeof item?.customerName === "string" && item.customerName.trim()) {
    return item.customerName.trim();
  }
  if (typeof item?.customer === "string" && item.customer.trim()) {
    return item.customer.trim();
  }
  if (item?.customer && typeof item.customer === "object") {
    const fromCustomer =
      item.customer.customerName || item.customer.name || "";
    if (String(fromCustomer).trim()) return String(fromCustomer).trim();
  }

  const rows = getCustomerPriceRows(item);
  const names = rows.map((row) => row.customerName).filter((name) => name && name !== "-");
  if (names.length > 0) return names[0];

  return "";
}

function getPrice(item) {
  const raw =
    item?.price ??
    item?.unitPrice ??
    item?.sellingPrice ??
    item?.amount ??
    null;
  if (raw != null && raw !== "") {
    const n = Number(raw);
    return Number.isFinite(n) ? n : raw;
  }

  const rows = getCustomerPriceRows(item);
  const prices = rows
    .map((row) => row.price)
    .filter((price) => price != null && price !== "" && price !== "-");
  if (prices.length === 0) return null;
  return prices[0];
}

function formatPriceDisplay(row) {
  if (row.price == null || row.price === "" || row.price === "-") return "-";
  return row.currency ? `${row.currency} ${row.price}` : String(row.price);
}

const Ecatalogue = () => {
  const { canView, canUpdate } = useModuleAccess("ecatalogue");
  const savedFilters = JSON.parse(
    sessionStorage.getItem("ecatalogueFilters") || "{}",
  );

  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(
    savedFilters.searchQuery || "",
  );
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || "",
  );
  const [filterCustomerName, setFilterCustomerName] = useState(
    savedFilters.filterCustomerName || "",
  );
  const [activeFilterCustomerName, setActiveFilterCustomerName] = useState(
    savedFilters.activeFilterCustomerName || "",
  );
  const [filterPriceType, setFilterPriceType] = useState(
    savedFilters.filterPriceType || "",
  );
  const [activeFilterPriceType, setActiveFilterPriceType] = useState(
    savedFilters.activeFilterPriceType || "",
  );
  const [filterMaterialCode, setFilterMaterialCode] = useState(
    savedFilters.filterMaterialCode || "",
  );
  const [activeFilterMaterialCode, setActiveFilterMaterialCode] = useState(
    savedFilters.activeFilterMaterialCode || "",
  );
  const [activeFilterStatus, setActiveFilterStatus] = useState(
    savedFilters.activeFilterStatus || "",
  );
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(
    savedFilters.currentPage || 1,
  );
  const [itemsPerPage, setItemsPerPage] = useState(
    savedFilters.itemsPerPage || 25,
  );
  const [sortBy, setSortBy] = useState(
    normalizeSortBy(savedFilters.sortBy || DEFAULT_SORT_BY),
  );
  const [sortOrder, setSortOrder] = useState(
    normalizeSortOrder(savedFilters.sortOrder || DEFAULT_SORT_ORDER),
  );
  const [viewMode, setViewMode] = useState(
    normalizeViewMode(savedFilters.viewMode || DEFAULT_VIEW_MODE),
  );
  const [customers, setCustomers] = useState([]);
  const [ecCounts, setEcCounts] = useState({
    totalCount: 0,
    activeCount: 0,
    inactiveCount: 0,
  });
  const [isCountsLoading, setIsCountsLoading] = useState(true);
  const [customerPricesModal, setCustomerPricesModal] = useState(null);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await axios.post(API_URL, {
        action: "get-all-customers",
        page: 1,
        limit: 5000,
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      if (isApiSuccess(responseData)) {
        setCustomers(extractCustomersList(responseData));
      }
    } catch (error) {
      console.error("Fetch customers error:", error);
    }
  }, []);

  const fetchEcatalogueCounts = useCallback(async () => {
    setIsCountsLoading(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-ecatalogue-counts",
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      const data =
        responseData?.data && typeof responseData.data === "object"
          ? responseData.data
          : responseData;

      if (isApiSuccess(responseData) || data?.totalCount != null) {
        setEcCounts({
          totalCount: Number(data.totalCount) || 0,
          activeCount: Number(data.activeCount) || 0,
          inactiveCount: Number(data.inactiveCount) || 0,
        });
      }
    } catch (error) {
      console.error("E-catalogue counts error:", error);
    } finally {
      setIsCountsLoading(false);
    }
  }, []);

  const fetchEcatalogues = useCallback(async () => {
    setIsLoading(true);
    try {
      const payload = {
        action: "get-all-ecatalogue",
        page: currentPage,
        limit: itemsPerPage,
        sortBy: normalizeSortBy(sortBy),
        sortOrder: normalizeSortOrder(sortOrder),
      };

      const trimmedSearch = String(activeSearchQuery || "").trim();
      if (trimmedSearch) payload.search = trimmedSearch;

      const trimmedCustomer = String(activeFilterCustomerName || "").trim();
      if (trimmedCustomer) payload.customerName = trimmedCustomer;

      const trimmedPriceType = String(activeFilterPriceType || "").trim();
      if (trimmedPriceType) payload.priceType = trimmedPriceType;

      const trimmedMaterialCode = String(activeFilterMaterialCode || "").trim();
      if (trimmedMaterialCode) payload.materialCode = trimmedMaterialCode;

      const trimmedStatus = String(activeFilterStatus || "").trim().toLowerCase();
      if (trimmedStatus) payload.status = trimmedStatus;

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      const {
        items: list,
        pagination,
        sortBy: responseSortBy,
        sortOrder: responseSortOrder,
      } = extractEcataloguePayload(responseData);

      const ok =
        isApiSuccess(responseData) || (Array.isArray(list) && list.length > 0);

      if (ok) {
        setItems(list);
        const total =
          typeof pagination.total === "number"
            ? pagination.total
            : typeof pagination.totalCount === "number"
              ? pagination.totalCount
              : list.length;
        setTotalCount(total);

        if (
          responseSortBy &&
          ECATALOGUE_SORT_FIELDS.includes(responseSortBy)
        ) {
          setSortBy(responseSortBy);
        }
        if (responseSortOrder === "asc" || responseSortOrder === "desc") {
          setSortOrder(responseSortOrder);
        }
      } else {
        toast.error(
          responseData?.message || "Failed to fetch e-catalogue",
          { autoClose: 2000 },
        );
        setItems([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Fetch e-catalogue error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        (typeof errData === "object" && errData?.message) ||
          "An error occurred while fetching e-catalogue",
        { autoClose: 2000 },
      );
      setItems([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    activeSearchQuery,
    activeFilterCustomerName,
    activeFilterPriceType,
    activeFilterMaterialCode,
    activeFilterStatus,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    fetchEcatalogueCounts();
  }, [fetchEcatalogueCounts]);

  useEffect(() => {
    fetchEcatalogues();
  }, [fetchEcatalogues]);

  useEffect(() => {
    sessionStorage.setItem(
      "ecatalogueFilters",
      JSON.stringify({
        searchQuery,
        activeSearchQuery,
        filterCustomerName,
        activeFilterCustomerName,
        filterPriceType,
        activeFilterPriceType,
        filterMaterialCode,
        activeFilterMaterialCode,
        activeFilterStatus,
        currentPage,
        itemsPerPage,
        sortBy,
        sortOrder,
        viewMode,
      }),
    );
  }, [
    searchQuery,
    activeSearchQuery,
    filterCustomerName,
    activeFilterCustomerName,
    filterPriceType,
    activeFilterPriceType,
    filterMaterialCode,
    activeFilterMaterialCode,
    activeFilterStatus,
    currentPage,
    itemsPerPage,
    sortBy,
    sortOrder,
    viewMode,
  ]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const startItem = items.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);
  const hasActiveFilters = Boolean(
    activeSearchQuery ||
      activeFilterCustomerName ||
      activeFilterPriceType ||
      activeFilterMaterialCode ||
      activeFilterStatus,
  );

  const isEcStatCardActive = (card) => {
    if (!card.filterValue) {
      return !activeFilterStatus;
    }
    return (
      String(activeFilterStatus || "").toLowerCase() === card.filterValue
    );
  };

  const handleEcStatCardClick = (card) => {
    if (!card.filterValue) {
      setActiveFilterStatus("");
    } else {
      setActiveFilterStatus(card.filterValue);
    }
    if (currentPage !== 1) setCurrentPage(1);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value) || 25);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    const next = Math.min(Math.max(1, page), totalPages);
    setCurrentPage(next);
  };

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i += 1) pages.push(i);
      return pages;
    }
    pages.push(1);
    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);
    if (currentPage <= 3) {
      start = 2;
      end = 4;
    } else if (currentPage >= totalPages - 2) {
      start = totalPages - 3;
      end = totalPages - 1;
    }
    if (start > 2) pages.push("ellipsis");
    for (let i = start; i <= end; i += 1) pages.push(i);
    if (end < totalPages - 1) pages.push("ellipsis");
    pages.push(totalPages);
    return pages;
  };

  const applyFilters = () => {
    setActiveSearchQuery(String(searchQuery || "").trim());
    setActiveFilterCustomerName(String(filterCustomerName || "").trim());
    setActiveFilterPriceType(String(filterPriceType || "").trim());
    setActiveFilterMaterialCode(String(filterMaterialCode || "").trim());
    setCurrentPage(1);
  };

  const handleClearFilter = () => {
    setFilterMaterialCode("");
    setFilterCustomerName("");
    setFilterPriceType("");
    setActiveFilterMaterialCode("");
    setActiveFilterCustomerName("");
    setActiveFilterPriceType("");
    if (currentPage !== 1) setCurrentPage(1);
  };

  const hasFieldFilters = Boolean(
    String(filterMaterialCode || "").trim() ||
      String(filterCustomerName || "").trim() ||
      String(filterPriceType || "").trim() ||
      String(activeFilterMaterialCode || "").trim() ||
      String(activeFilterCustomerName || "").trim() ||
      String(activeFilterPriceType || "").trim(),
  );

  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    if (String(newValue).trim() === "" && activeSearchQuery !== "") {
      setActiveSearchQuery("");
      setCurrentPage(1);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      applyFilters();
    }
  };

  const handleSort = (column) => {
    const apiField = normalizeSortBy(column);
    if (!ECATALOGUE_SORT_FIELDS.includes(apiField)) return;
    if (sortBy === apiField) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(apiField);
      setSortOrder("asc");
    }
    if (currentPage !== 1) setCurrentPage(1);
  };

  const getSortIcon = (column) => {
    const apiField = normalizeSortBy(column);
    if (!apiField || sortBy !== apiField) {
      return (
        <i
          className="fa-solid fa-sort ml-1"
          style={{ opacity: 0.35, fontSize: "11px" }}
        />
      );
    }
    return (
      <i
        className={`fa-solid fa-sort-${sortOrder === "asc" ? "up" : "down"} ml-1`}
        style={{ fontSize: "11px" }}
      />
    );
  };

  const renderSortableHeader = (column, label, style = {}) => (
    <th
      style={{ whiteSpace: "nowrap", cursor: "pointer", ...style }}
      onClick={() => handleSort(column)}
    >
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        {label}
        {getSortIcon(column)}
      </span>
    </th>
  );

  const renderActionButtons = (item) => {
    const id = getRecordId(item);
    if (!id) return null;
    return (
      <div
        align="center"
        style={{ whiteSpace: "nowrap" }}
        className="d-flex justify-content-center align-items-center"
      >
        {canView && (
          <Link
            to={`/ecatalogueView/${id}`}
            className="crsr_pntr action-icon-link mr-2"
            title="View"
          >
            <i className="fa-solid fa-eye" />
          </Link>
        )}
        {canUpdate && (
          <Link
            to={`/ecatalogueEdit/${id}`}
            className="crsr_pntr action-icon-link"
            title="Add Customer"
          >
            <i className="fa-solid fa-pen" />
          </Link>
        )}
      </div>
    );
  };

  const openCustomerPricesModal = (item, title) => {
    const rows = getCustomerPriceRows(item);
    if (rows.length <= 1) return;
    setCustomerPricesModal({
      title,
      materialCode: displayValue(item.materialCode),
      productName: displayValue(getProductName(item)),
      rows,
    });
  };

  const closeCustomerPricesModal = () => setCustomerPricesModal(null);

  const renderMoreCountBadge = (extraCount, onClick) => (
    <button
      type="button"
      className="btn btn-sm p-0 ml-1 border-0 align-baseline"
      onClick={onClick}
      title={`View ${extraCount} more`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: "24px",
        height: "20px",
        padding: "0 6px",
        borderRadius: "10px",
        backgroundColor: "#1199B2",
        color: "#fff",
        fontSize: "11px",
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      +{extraCount}
    </button>
  );

  const renderCustomerCell = (item) => {
    const rows = getCustomerPriceRows(item);
    if (rows.length === 0) return displayValue(getCustomerName(item));

    const firstName = rows[0].customerName;
    const extra = rows.length - 1;
    if (extra === 0) return firstName;

    return (
      <span className="d-inline-flex align-items-center flex-wrap">
        <span>{firstName}</span>
        {renderMoreCountBadge(extra, () =>
          openCustomerPricesModal(item, "Customer Prices"),
        )}
      </span>
    );
  };

  const renderPriceCell = (item) => {
    const rows = getCustomerPriceRows(item);
    if (rows.length === 0) {
      const price = getPrice(item);
      return price != null ? price : "-";
    }

    const firstPrice = rows[0].price;
    const extra = rows.length - 1;
    if (extra === 0) {
      return firstPrice;
    }

    return (
      <span className="d-inline-flex align-items-center">
        <span>{firstPrice}</span>
        {renderMoreCountBadge(extra, () =>
          openCustomerPricesModal(item, "Price Details"),
        )}
      </span>
    );
  };

  const renderPagination = () => (
    <div className="supply-pagination-container">
      <div className="pagination-wrapper">
        <div className="pagination-info">
          <label className="mb-0 mr-2" style={{ fontSize: "13px" }}>
            Show:
          </label>
          <select
            className="form-control form-control-sm"
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            style={{ width: "70px", display: "inline-block" }}
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <span className="ml-2" style={{ fontSize: "13px" }}>
            {startItem}-{endItem} of {totalCount}
          </span>
        </div>
        <nav>
          <ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              >
                «
              </button>
            </li>
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ‹
              </button>
            </li>
            {getPageNumbers().map((page, index) =>
              page === "ellipsis" ? (
                <li
                  key={`e-${index}`}
                  className="page-item disabled"
                >
                  <span className="page-link">...</span>
                </li>
              ) : (
                <li
                  key={page}
                  className={`page-item ${currentPage === page ? "active" : ""}`}
                >
                  <button
                    type="button"
                    className="page-link"
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                </li>
              ),
            )}
            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                type="button"
                className="page-link"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                ›
              </button>
            </li>
            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                type="button"
                className="page-link"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              >
                »
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );

  const renderGridView = () => (
    <div>
      <div className="row">
        {items.map((item, index) => {
          const id = getRecordId(item) || index;
          const images = getItemImages(item);
          const firstImageUrl = images[0]?.url || "";

          return (
            <div
              key={id}
              className="col-xl-3 col-lg-4 col-md-6 col-sm-6 mb-3"
            >
              <div
                className="card h-100"
                style={{
                  borderRadius: "10px",
                  border: "1px solid #e3e8ee",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                }}
              >
                <div
                  style={{
                    height: "160px",
                    backgroundColor: "#f4f7f9",
                    borderTopLeftRadius: "10px",
                    borderTopRightRadius: "10px",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "8px",
                  }}
                >
                  {firstImageUrl ? (
                    <img
                      src={firstImageUrl}
                      alt={getProductName(item) || "catalogue"}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <i
                      className="fa-solid fa-book-open"
                      style={{ fontSize: "42px", color: "#b0bec5" }}
                    />
                  )}
                </div>
                <div className="card-body" style={{ padding: "14px 16px" }}>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div
                      style={{
                        color: "#1199B2",
                        fontSize: "12px",
                        fontWeight: 700,
                      }}
                    >
                      {displayValue(item.materialCode)}
                    </div>
                    {renderStatusBadge(item.status)}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#212529",
                      marginBottom: "8px",
                      minHeight: "40px",
                      wordBreak: "break-word",
                    }}
                  >
                    {displayValue(getProductName(item))}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6c757d",
                      marginBottom: "4px",
                    }}
                  >
                    <strong>Customer:</strong> {renderCustomerCell(item)}
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#212529",
                      fontWeight: 700,
                      marginBottom: "6px",
                    }}
                  >
                    Price: {renderPriceCell(item)}
                  </div>
                   <div
                    style={{
                     fontSize: "13px",
                      color: "#212529",
                      fontWeight: 700,
                      marginBottom: "6px",
                      }}>
                      Stock Qty: {displayValue(item.stockQuantity)}
                   </div>
                  <div
                    className="d-flex justify-content-between"
                    style={{
                      fontSize: "12px",
                      color: "#6c757d",
                      marginBottom: "6px",
                    }}
                  >
                    <span>
                      <strong>HSN:</strong> {displayValue(item.hsnCode)}
                    </span>
                    <span>
                      <strong>UOM:</strong> {displayValue(item.uom)}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#6c757d",
                      marginBottom: "10px",
                    }}
                  >
                    Updated:{" "}
                    {formatDateToDisplay(item.updatedAt || item.createdAt)}
                  </div>
                  <div className="d-flex justify-content-end">
                    {renderActionButtons(item)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {renderPagination()}
    </div>
  );

  const renderListView = () => (
    <div className="supply-table-container">
      <div className="table-responsive">
        <table
          id="ecatalogues"
          width="100%"
          className="table table-striped table-bordered txt_lft"
        >
          <thead>
            <tr>
              <th
                style={{
                  whiteSpace: "nowrap",
                  width: "40px",
                  minWidth: "40px",
                }}
              >
                S.No
              </th>
              <th
                style={{
                  width: "48px",
                  minWidth: "48px",
                  maxWidth: "52px",
                  textAlign: "center",
                }}
              >
                Image
              </th>
              {renderSortableHeader("materialCode", "Material Code", {
                width: "110px",
                minWidth: "110px",
                maxWidth: "120px",
              })}
              {renderSortableHeader("productName", "Product Name", {
                width: "260px",
                minWidth: "220px",
                maxWidth: "320px",
              })}
              {renderSortableHeader("customerName", "Customer", {
                width: "200px",
                minWidth: "200px",
                maxWidth: "240px",
              })}
              {renderSortableHeader("price", "Price", {
                width: "70px",
                minWidth: "70px",
                maxWidth: "80px",
              })}
              {renderSortableHeader("hsnCode", "HSN", {
                width: "70px",
                minWidth: "70px",
                maxWidth: "80px",
              })}
              {renderSortableHeader("uom", "UOM", {
                width: "55px",
                minWidth: "55px",
                maxWidth: "65px",
              })}
              {renderSortableHeader("stockQuantity", "Stock Qty", {
               width: "90px",
               minWidth: "80px",
               maxWidth: "100px",
              })}
              {renderSortableHeader("updatedAt", "Updated At", {
                width: "95px",
                minWidth: "95px",
                maxWidth: "100px",
              })}
              <th
                style={{
                  width: "70px",
                  minWidth: "70px",
                  maxWidth: "80px",
                  textAlign: "center",
                }}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => {
              const id = getRecordId(item) || index;
              const images = getItemImages(item);
              const firstImageUrl = images[0]?.url || "";

              return (
                <tr key={id}>
                  <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td style={{ width: "48px", textAlign: "center" }}>
                    <div
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "6px",
                        overflow: "hidden",
                        backgroundColor: "#f4f7f9",
                        border: "1px solid #e3e8ee",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {firstImageUrl ? (
                        <img
                          src={firstImageUrl}
                          alt={getProductName(item) || "catalogue"}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <i
                          className="fa-solid fa-book-open"
                          style={{ fontSize: "14px", color: "#b0bec5" }}
                        />
                      )}
                    </div>
                  </td>
                  <td
                    style={{
                      whiteSpace: "nowrap",
                      maxWidth: "120px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={displayValue(item.materialCode)}
                  >
                    {displayValue(item.materialCode)}
                  </td>
                  <td
                    style={{
                      maxWidth: "320px",
                      minWidth: "220px",
                      wordWrap: "break-word",
                      whiteSpace: "normal",
                      overflow: "hidden",
                    }}
                    title={displayValue(getProductName(item))}
                  >
                    {displayValue(getProductName(item))}
                  </td>
                  <td
                    style={{
                      maxWidth: "240px",
                      wordWrap: "break-word",
                      whiteSpace: "normal",
                      overflow: "hidden",
                    }}
                  >
                    {renderCustomerCell(item)}
                  </td>
                  <td
                    style={{
                      whiteSpace: "nowrap",
                      fontWeight: 600,
                      maxWidth: "80px",
                    }}
                  >
                    {renderPriceCell(item)}
                  </td>
                  <td
                    style={{
                      whiteSpace: "nowrap",
                      maxWidth: "80px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={displayValue(item.hsnCode)}
                  >
                    {displayValue(item.hsnCode)}
                  </td>
                  <td
                    style={{
                      whiteSpace: "nowrap",
                      maxWidth: "65px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={displayValue(item.uom)}
                  >
                    {displayValue(item.uom)}
                  </td>
                  <td
  style={{
    whiteSpace: "nowrap",
    maxWidth: "100px",
    fontWeight: 600,
  }}
>
  {displayValue(item.stockQuantity)}
</td>
                  <td style={{ whiteSpace: "nowrap", maxWidth: "100px" }}>
                    {formatDateToDisplay(item.updatedAt || item.createdAt)}
                  </td>
                  <td style={{ width: "70px", textAlign: "center" }}>
                    {renderActionButtons(item)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {renderPagination()}
    </div>
  );

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">E-Catalogue</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="row stat-card-row">
                {isCountsLoading
                  ? EC_STAT_CARDS.map((card) => (
                      <EcStatCardSkeleton key={card.key} />
                    ))
                  : EC_STAT_CARDS.map((card) => (
                      <EcStatCard
                        key={card.key}
                        label={card.label}
                        value={ecCounts[card.key]}
                        titleColor={card.titleColor}
                        valueColor={card.valueColor}
                        isActive={isEcStatCardActive(card)}
                        onClick={() => handleEcStatCardClick(card)}
                      />
                    ))}
              </div>

              <div>
                <div className="card-body">
                  <div className="row mb-3 align-items-end">
                    <div className="col-md-3">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Search
                        </label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search material, name..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyDown={handleSearchKeyDown}
                          />
                          <div className="input-group-append">
                            <div
                              className="input-group-text crsr_pntr"
                              onClick={applyFilters}
                              style={{ cursor: "pointer" }}
                              title="Search"
                            >
                              <i className="fa-solid fa-search" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-2">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Material Code
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. 002-002-0001"
                          value={filterMaterialCode}
                          onChange={(e) =>
                            setFilterMaterialCode(e.target.value)
                          }
                          onKeyDown={handleSearchKeyDown}
                        />
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Customer
                        </label>
                        <select
                          className="form-control"
                          value={filterCustomerName}
                          onChange={(e) => {
                            setFilterCustomerName(e.target.value);
                            setActiveFilterCustomerName(e.target.value);
                            setCurrentPage(1);
                          }}
                        >
                          <option value="">All</option>
                          {customers.map((customer) => (
                            <option
                              key={customer._id}
                              value={customer.customerName}
                            >
                              {customer.customerName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="col-md-2">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Currency
                        </label>
                        <select
                          className="form-control"
                          value={filterPriceType}
                          onChange={(e) => {
                            setFilterPriceType(e.target.value);
                            setActiveFilterPriceType(e.target.value);
                            setCurrentPage(1);
                          }}
                        >
                          <option value="">All</option>
                          {PRICE_TYPE_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="col-md-2">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1 d-block"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          &nbsp;
                        </label>
                        <div className="d-flex align-items-center flex-wrap">
                          {hasFieldFilters && (
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm mr-2 mb-1"
                              onClick={handleClearFilter}
                            >
                              <i className="fa-solid fa-times mr-1" />
                              Clear Filter
                            </button>
                          )}
                          <div className="btn-group mb-1" role="group">
                            <button
                              type="button"
                              className={`btn btn-sm ${
                                viewMode === "grid"
                                  ? "btn-info"
                                  : "btn-outline-secondary"
                              }`}
                              onClick={() => setViewMode("grid")}
                              title="Grid view"
                              style={{
                                backgroundColor:
                                  viewMode === "grid" ? "#1199B2" : undefined,
                                borderColor:
                                  viewMode === "grid" ? "#1199B2" : undefined,
                                color: viewMode === "grid" ? "#fff" : undefined,
                              }}
                            >
                              <i className="fa-solid fa-th mr-1" />
                              Grid
                            </button>
                            <button
                              type="button"
                              className={`btn btn-sm ${
                                viewMode === "list"
                                  ? "btn-info"
                                  : "btn-outline-secondary"
                              }`}
                              onClick={() => setViewMode("list")}
                              title="List view"
                              style={{
                                backgroundColor:
                                  viewMode === "list" ? "#1199B2" : undefined,
                                borderColor:
                                  viewMode === "list" ? "#1199B2" : undefined,
                                color: viewMode === "list" ? "#fff" : undefined,
                              }}
                            >
                              <i className="fa-solid fa-list mr-1" />
                              List
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {isLoading ? (
                    <div className="text-center p-4">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  ) : items.length === 0 && !hasActiveFilters ? (
                    <div className="nocontent_div text-center p-4">
                      There are no e-catalogue records yet.
                    </div>
                  ) : items.length === 0 && hasActiveFilters ? (
                    <div className="nocontent_div text-center p-4">
                      No e-catalogue found matching your filters.
                    </div>
                  ) : viewMode === "grid" ? (
                    renderGridView()
                  ) : (
                    renderListView()
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {customerPricesModal ? (
        <>
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            role="dialog"
            aria-labelledby="ecCustomerPricesModalLabel"
            onClick={closeCustomerPricesModal}
          >
            <div
              className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg"
              style={{ maxWidth: "760px" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="modal-content"
                style={{
                  borderRadius: "12px",
                  overflow: "hidden",
                  border: "none",
                }}
              >
                <div
                  className="modal-header"
                  style={{
                    backgroundColor: "#1199B2",
                    color: "#fff",
                    borderTopLeftRadius: "12px",
                    borderTopRightRadius: "12px",
                  }}
                >
                  <h5
                    className="modal-title"
                    id="ecCustomerPricesModalLabel"
                  >
                    {customerPricesModal.title}
                  </h5>
                  <button
                    type="button"
                    className="close"
                    aria-label="Close"
                    onClick={closeCustomerPricesModal}
                    style={{ color: "#fff" }}
                  >
                    <i className="fa-solid fa-xmark" />
                  </button>
                </div>
                <div className="modal-body">
                  <div
                    className="text-muted mb-3"
                    style={{ fontSize: "13px" }}
                  >
                    {customerPricesModal.materialCode} —{" "}
                    {customerPricesModal.productName}
                  </div>
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm mb-0">
                      <thead>
                        <tr
                          style={{
                            backgroundColor: "#003366",
                            color: "#fff",
                          }}
                        >
                          <th style={{ fontSize: "12px", width: "48px" }}>
                            S.No
                          </th>
                          <th style={{ fontSize: "12px" }}>Customer</th>
                          <th style={{ fontSize: "12px", width: "110px" }}>
                            Basic Price
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {customerPricesModal.rows.map((row, index) => (
                          <tr key={row.key}>
                            <td>{index + 1}</td>
                            <td>{row.customerName}</td>
                            <td style={{ fontWeight: 600 }}>
                              {formatPriceDisplay(row)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div
                  className="modal-footer"
                  style={{
                    borderBottomLeftRadius: "12px",
                    borderBottomRightRadius: "12px",
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeCustomerPricesModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      ) : null}
    </div>
  );
};

export default Ecatalogue;
